import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Lock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { api, apiErrorMessage } from "../lib/api";
import {
  TEXT, clearDraft, dayLabel, dayTime, loadDraft, missing as findMissing, missingLine, saveDraft, shortDay,
  tokenFromHash, toggleChoice,
} from "../lib/initialAssessment";

/**
 * `/evaluare-initiala#t=…` — the initial assessment (15.10.2026): FPED's
 * Google Form, on one page, three sections stacked (decided by FPED). The
 * token sits after `#`, so it never reaches a server's logs; the page sends it
 * in the body. Answers are kept in the browser until sent, under the record
 * rather than the link, so a link sent again does not lose them.
 */
export default function EvaluareInitiala() {
  const [token] = useState(() => tokenFromHash(typeof window !== "undefined" ? window.location.hash : ""));
  const [page, setPage] = useState(null); // null loading · {state…} · "invalid"
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) { setPage("invalid"); return; }
    api.post("/initial-assessment/open", { token })
      .then((r) => setPage(r.data))
      .catch(() => setPage("invalid"));
  }, [token]);

  return (
    <div className="min-h-screen bg-[#F5F6F6] flex flex-col" data-testid="assessment-page">
      <SEO title="Evaluarea inițială | FPED" noindex />
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        {page === null && <div className="py-24 text-center text-[#6B7280]"><Loader2 className="mx-auto animate-spin" /></div>}
        {page === "invalid" && <Invalid />}
        {done && <Thanks name={done.first_name} />}
        {!done && page && page !== "invalid" && page.state === "completed" && <Already at={page.completed_at} />}
        {!done && page && page !== "invalid" && page.state === "closed" && <Closed page={page} token={token} />}
        {!done && page && page !== "invalid" && page.state === "open" && (
          <Questionnaire page={page} token={token} onDone={setDone} />
        )}
      </main>
      <Footer />
    </div>
  );
}

function Card({ children, center = false }) {
  return (
    <div className={`rounded-3xl border border-[#E5E7EB] bg-white p-6 sm:p-8 md:p-10 ${center ? "text-center" : ""}`}>{children}</div>
  );
}

function Invalid() {
  return (
    <Card>
      <h1 className="font-display text-2xl font-bold text-[#373A45]">Linkul nu mai e valid</h1>
      <p className="mt-3 text-[15px] leading-relaxed text-[#373A45]">
        Folosește linkul din cel mai recent email primit de la noi, sau intră în{" "}
        <a href="/portal" className="font-semibold text-[#7A4986] underline">portalul cursului</a>: acolo găsești chestionarul pe
        cardul grupei. Dacă nu găsești emailul, scrie-ne la{" "}
        <a href="mailto:contact@fped.org" className="font-semibold text-[#7A4986]">contact@fped.org</a>.
      </p>
    </Card>
  );
}

function Thanks({ name }) {
  return (
    <Card center>
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-[#EAF4EE] text-[#2F6B4E]"><CheckCircle2 size={24} /></div>
      <h1 className="mt-5 font-display text-2xl font-bold text-[#373A45]" data-testid="assessment-thanks">
        {name ? `Mulțumim, ${name}!` : "Mulțumim!"}
      </h1>
      <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-[#373A45]">
        Am primit răspunsurile tale. Formatorul grupei le citește înainte de prima sesiune, ca să adapteze exemplele și
        ritmul cursului.
      </p>
      <p className="mt-3 text-[15px] font-semibold text-[#373A45]">Ne vedem la curs!</p>
    </Card>
  );
}

function Already({ at }) {
  return (
    <Card center>
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-[#EAF4EE] text-[#2F6B4E]"><CheckCircle2 size={24} /></div>
      <h1 className="mt-5 font-display text-2xl font-bold text-[#373A45]">Ai trimis deja chestionarul pe {shortDay(at)}. Mulțumim!</h1>
      <p className="mt-3 text-[15px] text-[#373A45]">
        Dacă vrei să schimbi ceva, scrie-ne la <a href="mailto:contact@fped.org" className="font-semibold text-[#7A4986]">contact@fped.org</a>.
      </p>
    </Card>
  );
}

function Closed({ page, token }) {
  const [asked, setAsked] = useState(page.request_at ? { new: false, at: page.request_at } : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const ask = async () => {
    setBusy(true);
    setError("");
    try {
      setAsked((await api.post("/initial-assessment/request-reopen", { token })).data);
    } catch (err) {
      setError(apiErrorMessage(err, "Nu am putut trimite cererea. Încearcă din nou."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[#F0E7F4] text-[#7A4986]"><Lock size={20} /></div>
      <h1 className="mt-4 font-display text-2xl font-bold text-[#373A45]">Chestionarul s-a închis pe {dayLabel(page.deadline)}</h1>
      <p className="mt-3 text-[15px] leading-relaxed text-[#373A45]">
        Chestionarul de evaluare inițială face parte din cerințele programului acreditat, așa că trebuie completat de toți
        cursanții. Cere-ne să ți-l redeschidem și îți trimitem un link nou pe email.
      </p>
      {asked ? (
        <p className="mt-5 rounded-2xl bg-[#EAF4EE] px-5 py-4 text-sm text-[#2F6B4E]" data-testid="assessment-asked">
          {asked.new
            ? "Am primit cererea ta. Îți trimitem linkul nou pe email în cel mai scurt timp, de obicei în aceeași zi lucrătoare."
            : `Ai cerut deja redeschiderea pe ${dayTime(asked.at)}. Îți scriem în curând.`}
        </p>
      ) : (
        <button type="button" onClick={ask} disabled={busy} data-testid="assessment-ask"
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#965EA7] px-6 py-3 font-semibold text-white hover:bg-[#7A4986] disabled:opacity-60">
          {busy && <Loader2 size={16} className="animate-spin" />} Cere redeschiderea
        </button>
      )}
      {error && <p className="mt-3 text-sm text-[#c0483f]" role="alert">{error}</p>}
    </Card>
  );
}

// ------------------------------------------------------------------ the form

function Questionnaire({ page, token, onDone }) {
  const def = page.questionnaire;
  const [answers, setAnswers] = useState(() => loadDraft(page.draft_key) || { ...page.prefill });
  const [marked, setMarked] = useState([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const refs = useRef({});

  useEffect(() => { saveDraft(page.draft_key, answers); }, [answers, page.draft_key]);

  const set = useCallback((id, value) => {
    setAnswers((a) => ({ ...a, [id]: value }));
    setMarked((m) => m.filter((x) => x !== id));
  }, []);

  const mark = (ids, text) => {
    setMarked(ids);
    setMessage(text);
    const first = ids.map((id) => refs.current[id]).find(Boolean);
    first?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const gaps = findMissing(def, answers);
    if (gaps.length) { mark(gaps, missingLine(gaps.length)); return; }
    setBusy(true);
    setMessage("");
    try {
      const r = await api.post("/initial-assessment/submit", { token, answers });
      clearDraft(page.draft_key);
      window.scrollTo({ top: 0, behavior: "smooth" });
      onDone(r.data);
    } catch (err) {
      const missingIds = err?.response?.data?.missing;
      if (Array.isArray(missingIds) && missingIds.length) mark(missingIds, err.response.data.detail);
      else setMessage(err?.response ? apiErrorMessage(err, TEXT.network) : TEXT.network);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="assessment-form">
      <Card>
        <h1 className="font-display text-2xl md:text-3xl font-bold leading-tight text-[#373A45]" style={{ textWrap: "balance" }}>{def.title}</h1>
        <p className="mt-3 text-[15px] leading-relaxed text-[#373A45]">{def.intro}</p>
        <p className="mt-3 text-sm leading-relaxed text-[#6B7280]">{TEXT.note(page.group_name)}</p>
        {page.reopened ? (
          <Note>{TEXT.reopened(dayLabel(page.deadline))}</Note>
        ) : page.after_start && page.deadline ? (
          <Note>{TEXT.started(dayLabel(page.deadline))}</Note>
        ) : null}
      </Card>

      {def.sections.map((section) => (
        <section key={section.key} className="mt-6 rounded-3xl border border-[#E5E7EB] bg-white p-6 sm:p-8" aria-labelledby={`sec-${section.key}`}>
          <h2 id={`sec-${section.key}`} className="font-display text-xl font-bold text-[#373A45]">{section.title}</h2>
          {section.key === "about" && <p className="mt-1 text-sm text-[#6B7280]">{TEXT.prefilled}</p>}
          <div className="mt-6 grid gap-8">
            {section.questions.map((q) => (
              <div key={q.id} ref={(el) => { refs.current[q.id] = el; }} data-testid={`q-${q.id}`}
                className={marked.includes(q.id) ? "rounded-2xl ring-2 ring-[#c0483f]/60 ring-offset-8" : ""}>
                <Question q={q} answers={answers} set={set} />
                {marked.includes(q.id) && <p className="mt-2 text-sm text-[#c0483f]">{TEXT.answerThis}</p>}
              </div>
            ))}
          </div>
        </section>
      ))}

      <div className="mt-6 rounded-3xl border border-[#E5E7EB] bg-white p-6 sm:p-8">
        <p className="text-sm text-[#6B7280]">{TEXT.saved}</p>
        {message && (
          <p className="mt-4 flex items-start gap-2 rounded-xl bg-[#c0483f]/10 px-4 py-3 text-sm text-[#a33a32]" role="alert" data-testid="assessment-message">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {message}
          </p>
        )}
        <button type="submit" disabled={busy} data-testid="assessment-submit"
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#965EA7] px-8 py-3 text-base font-semibold text-white hover:bg-[#7A4986] disabled:opacity-60">
          {busy && <Loader2 size={18} className="animate-spin" />} Trimite
        </button>
      </div>
    </form>
  );
}

function Note({ children }) {
  return <p className="mt-4 rounded-2xl bg-[#F0E7F4] px-5 py-3 text-sm font-semibold text-[#7A4986]">{children}</p>;
}

const FIELD = "w-full rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-4 py-3 text-[15px] text-[#373A45] outline-none focus:bg-white focus:border-[#965EA7]";

function Label({ q }) {
  return (
    <>
      <span className="block text-[15px] font-semibold text-[#373A45]">{q.label}</span>
      {q.help && <span className="mt-0.5 block text-sm text-[#6B7280]">{q.help}</span>}
    </>
  );
}

function Question({ q, answers, set }) {
  const value = answers[q.id];
  if (q.type === "text" || q.type === "long") {
    const Tag = q.type === "long" ? "textarea" : "input";
    return (
      <label className="block">
        <Label q={q} />
        <Tag value={value || ""} onChange={(e) => set(q.id, e.target.value)} maxLength={q.max}
          rows={q.type === "long" ? 3 : undefined} className={`${FIELD} mt-2`} />
      </label>
    );
  }
  if (q.type === "single") {
    return (
      <fieldset>
        <legend><Label q={q} /></legend>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {q.options.map((o) => (
            <Choice key={o.key} type="radio" name={q.id} checked={value === o.key} onChange={() => set(q.id, o.key)} label={o.label} />
          ))}
        </div>
      </fieldset>
    );
  }
  if (q.type === "multi") {
    const list = Array.isArray(value) ? value : [];
    const otherOn = q.other && list.includes(q.other);
    return (
      <fieldset>
        <legend><Label q={q} /></legend>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {q.options.map((o) => (
            <Choice key={o.key} type="checkbox" checked={list.includes(o.key)}
              onChange={() => set(q.id, toggleChoice(list, o.key, q.alone))}
              label={o.key === q.other ? `${o.label}:` : o.label} />
          ))}
        </div>
        {otherOn && (
          <input value={answers[`${q.id}_other`] || ""} onChange={(e) => set(`${q.id}_other`, e.target.value)} maxLength={120}
            placeholder="Scrie aici" aria-label={`${q.label}: altele`} className={`${FIELD} mt-2`} />
        )}
      </fieldset>
    );
  }
  if (q.type === "scale") {
    return (
      <fieldset>
        <legend><Label q={q} /></legend>
        <Scale name={q.id} min={q.min} max={q.max} value={value} onChange={(v) => set(q.id, v)} low={q.low} high={q.high} />
      </fieldset>
    );
  }
  if (q.type === "grid") {
    const grid = value && typeof value === "object" ? value : {};
    return (
      <fieldset>
        <legend><Label q={q} /></legend>
        <p className="mt-1 text-sm text-[#6B7280]">1 = deloc sigur · 5 = foarte sigur</p>
        <div className="mt-3 grid gap-3">
          {q.rows.map((row) => (
            <div key={row.key} className="rounded-2xl bg-[#F5F6F6] px-4 py-3 sm:flex sm:items-center sm:gap-4">
              <span className="block text-sm text-[#373A45] sm:flex-1">{row.label}</span>
              <Scale name={`${q.id}-${row.key}`} min={q.min} max={q.max} value={grid[row.key]} compact
                onChange={(v) => set(q.id, { ...grid, [row.key]: v })} label={row.label} />
            </div>
          ))}
        </div>
      </fieldset>
    );
  }
  return null;
}

function Choice({ type, name, checked, onChange, label }) {
  return (
    <label className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 text-[15px] transition-colors focus-within:ring-2 focus-within:ring-[#965EA7]
      ${checked ? "border-[#965EA7] bg-[#F0E7F4] text-[#373A45]" : "border-[#E5E7EB] bg-white text-[#373A45] hover:border-[#965EA7]"}`}>
      <input type={type} name={name} checked={checked} onChange={onChange} className="mt-1 h-4 w-4 shrink-0 accent-[#965EA7]" />
      <span>{label}</span>
    </label>
  );
}

function Scale({ name, min, max, value, onChange, low, high, compact = false, label }) {
  const steps = [];
  for (let i = min; i <= max; i += 1) steps.push(i);
  return (
    <div className={compact ? "mt-2 sm:mt-0" : "mt-3"}>
      <div className="flex gap-2" role="radiogroup" aria-label={label}>
        {steps.map((n) => (
          <label key={n} className={`grid h-11 w-11 cursor-pointer place-items-center rounded-xl border text-[15px] font-semibold focus-within:ring-2 focus-within:ring-[#965EA7]
            ${Number(value) === n ? "border-[#965EA7] bg-[#965EA7] text-white" : "border-[#E5E7EB] bg-white text-[#373A45] hover:border-[#965EA7]"}`}>
            <input type="radio" name={name} checked={Number(value) === n} onChange={() => onChange(n)} className="sr-only" />
            {n}
          </label>
        ))}
      </div>
      {(low || high) && (
        <div className="mt-1.5 flex max-w-[15rem] justify-between text-xs text-[#6B7280]"><span>{low}</span><span>{high}</span></div>
      )}
    </div>
  );
}
